import {Injectable, OnDestroy} from '@angular/core';

@Injectable()
export class OneTrustService implements OnDestroy {

    /** OneTrust category ID for Performance / Analytics */
    private readonly PERFORMANCE_GROUP = ',2,';

    /** Microsoft Clarity project ID - injected server-side as window.CLARITY_ID from CLARITY_CODE env var. */
    private readonly CLARITY_ID: string = window.CLARITY_ID || '';
    private consentListenerAttached = false;
    private clarityScript?: HTMLScriptElement;
    private clarityLoading = false;


    // Store handler reference so it can be removed
    private readonly onConsentUpdated = () => {
        this.applyCurrentConsent();
    };

    init(): void {
        // Ensure gtag stub exists early so "denied" updates work even before opt-in
        this.ensureGtagExists();

        // Deny until user opts in
        window.gtag?.('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            // Wait briefly for an update
            wait_for_update: 500
        });

        this.listenForConsentChanges();
        this.applyCurrentConsent();
    }

    ngOnDestroy(): void {
        this.detachConsentListener();
    }

    // Google Consent Mode (updates only)
    private ensureGtagExists(): void {
        if (window.dataLayer && window.gtag) {
            return;
        }

        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer!.push(arguments);
        };
    }

    private updateGtmConsent(groups: string): void {
        const performanceGranted = groups.includes(this.PERFORMANCE_GROUP);

        window.gtag?.('consent', 'update', {
            analytics_storage: performanceGranted ? 'granted' : 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
        });
    }

    // Microsoft Clarity (controlled by OneTrust)
    private loadClarity(): void {
        if (!this.CLARITY_ID || window.clarityLoaded || this.clarityLoading) {
            return;
        }

        this.clarityLoading = true;

        window.clarity =
            window.clarity ||
            function () {
                (window.clarity!.q = window.clarity!.q || []).push(arguments);
            };

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${this.CLARITY_ID}`;

        script.onload = () => {
            // Re-check consent before enabling
            const groups = window.OptanonActiveGroups || '';
            if (!groups.includes(this.PERFORMANCE_GROUP)) {
                // Consent revoked while loading → abort
                this.cleanupClarityScript(script);
                return;
            }

            window.clarityLoaded = true;
            this.clarityLoading = false;
        };

        script.onerror = () => {
            this.cleanupClarityScript(script);
        };

        this.clarityScript = script;
        document.head.appendChild(script);
    }

    // OneTrust integration
    private applyCurrentConsent(): void {
        const groups = window.OptanonActiveGroups || '';
        const performanceGranted = groups.includes(this.PERFORMANCE_GROUP);

        // Always ensure gtag exists before any consent updates (so denied updates apply)
        this.ensureGtagExists();

        // Always update consent (granted/denied) based on current groups
        this.updateGtmConsent(groups);

        if (performanceGranted) {
            // Load Clarity only when Performance is granted
            this.loadClarity();
        } else {
            // Stop/cleanup Clarity when Performance is not granted
            this.disableClarity();
        }
    }

    private listenForConsentChanges(): void {
        if (this.consentListenerAttached) {
            return;
        }

        window.addEventListener('OneTrustGroupsUpdated', this.onConsentUpdated);

        this.consentListenerAttached = true;
    }

    private detachConsentListener(): void {
        if (!this.consentListenerAttached) {
            return;
        }

        window.removeEventListener('OneTrustGroupsUpdated', this.onConsentUpdated);

        this.consentListenerAttached = false;
    }

    private disableClarity(): void {
        // Stop active tracking (if loaded)
        try {
            window.clarity?.('stop');
        } catch {
            // ignore if API not available yet
        }

        // Cancel in-flight load
        if (this.clarityScript) {
            this.cleanupClarityScript(this.clarityScript);
        }

        // Reset global state
        delete window.clarity;
        window.clarityLoaded = false;
        this.clarityLoading = false;
    }

    private cleanupClarityScript(script: HTMLScriptElement): void {
        script.onload = null;
        script.onerror = null;

        if (script.parentNode) {
            script.parentNode.removeChild(script);
        }

        this.clarityScript = undefined;
        this.clarityLoading = false;
    }
}
